import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "./MapView.css";

// Set the access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const Marker = ({ map, provider, onProviderClick }) => {
    const markerRef = useRef();

    useEffect(() => {
        if (!map || !provider.latitude || !provider.longitude) return;

        // Create the main container for the custom marker
        const markerElement = document.createElement("div");
        markerElement.className = "custom-marker-container";

        // Set the inner HTML for the marker's content
        markerElement.innerHTML = `
            <div class="marker-content">
                <div class="marker-name">${provider.business_name}</div>
                ${
                    provider.average_rating > 0
                        ? `<div class="marker-rating">★ ${parseFloat(
                              provider.average_rating
                          ).toFixed(1)}</div>`
                        : ""
                }
            </div>
            <div class="marker-pin"></div>
        `;

        // Add click listener to the entire marker
        markerElement.addEventListener("click", (e) => {
            e.stopPropagation();
            // Open provider page in a new tab
            window.open(`/provider/${provider.id}`, "_blank");
        });

        // Create the Mapbox marker and add it to the map
        const marker = new mapboxgl.Marker(markerElement)
            .setLngLat([
                parseFloat(provider.longitude),
                parseFloat(provider.latitude),
            ])
            .addTo(map);

        markerRef.current = marker;

        // Cleanup function to remove the marker
        return () => {
            if (markerRef.current) {
                markerRef.current.remove();
            }
        };
    }, [map, provider]);

    return null;
};

const UserLocationMarker = ({ map, userLocation }) => {
    const markerRef = useRef();

    useEffect(() => {
        if (!map || !userLocation) return;

        const markerElement = document.createElement("div");
        markerElement.className = "user-location-marker";
        markerElement.innerHTML = '<div class="user-location-pulse"></div>';

        const marker = new mapboxgl.Marker(markerElement)
            .setLngLat([userLocation.lng, userLocation.lat])
            .addTo(map);

        markerRef.current = marker;

        return () => {
            if (markerRef.current) {
                markerRef.current.remove();
            }
        };
    }, [map, userLocation]);

    return null;
};

const MapView = ({ providers, userLocation, onProviderClick }) => {
    const mapContainerRef = useRef();
    const mapRef = useRef();
    const [mapLoaded, setMapLoaded] = useState(false);

    // Effect to initialize the map ONCE
    useEffect(() => {
        if (mapRef.current) return; // Initialize map only once

        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: "mapbox://styles/mapbox/streets-v11",
            center: [-98.5795, 39.8283], // Default center
            zoom: 4,
        });

        map.addControl(new mapboxgl.NavigationControl(), "top-right");
        map.on("load", () => setMapLoaded(true));
        mapRef.current = map;

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    // Effect to fly to user's location when it's available
    useEffect(() => {
        if (mapLoaded && mapRef.current && userLocation) {
            mapRef.current.flyTo({
                center: [userLocation.lng, userLocation.lat],
                zoom: 12,
            });
        }
    }, [userLocation, mapLoaded]);

    // Effect to calculate bounds and fit map when providers change
    useEffect(() => {
        if (!mapLoaded || !mapRef.current || !providers.length) return;

        const validProviders = providers.filter(
            (p) => p.latitude && p.longitude
        );
        if (validProviders.length === 0) return;

        const bounds = new mapboxgl.LngLatBounds();
        if (userLocation) {
            bounds.extend([userLocation.lng, userLocation.lat]);
        }
        validProviders.forEach((p) => {
            bounds.extend([parseFloat(p.longitude), parseFloat(p.latitude)]);
        });

        if (!bounds.isEmpty()) {
            mapRef.current.fitBounds(bounds, {
                padding: { top: 50, bottom: 50, left: 50, right: 50 },
                maxZoom: 15,
                duration: 1000,
            });
        }
    }, [providers, mapLoaded, userLocation]);

    return (
        <div className="map-container">
            <div
                ref={mapContainerRef}
                style={{ width: "100%", height: "100%" }}
            />
            {mapRef.current && mapLoaded && (
                <>
                    {userLocation && (
                        <UserLocationMarker
                            map={mapRef.current}
                            userLocation={userLocation}
                        />
                    )}
                    {providers
                        .filter((p) => p.latitude && p.longitude)
                        .map((provider) => (
                            <Marker
                                key={provider.id}
                                map={mapRef.current}
                                provider={provider}
                                onProviderClick={onProviderClick} // Pass the navigation function directly
                            />
                        ))}
                </>
            )}
        </div>
    );
};

export default MapView;
